import {Component, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Title} from '@angular/platform-browser';
import {DataService} from '../../../Services/data.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {AppConfigService} from '../../../Services/app-config.service';
import {ConfirmDialogService} from '../../../Services/confirm-dialog.service';

@Component({
  selector: 'app-gusto-dashboard',
  templateUrl: './gusto-dashboard.component.html',
  styleUrls: ['./gusto-dashboard.component.css'],
  standalone: false
})
export class GustoDashboardComponent implements OnInit {

  // ── Connection / config ──────────────────────────────────────────────────
  config: any = null;
  companies: any[] = [];
  apiBaseUrl: string = '';
  buttonsDisabled = false;
  isPostingGl = false;

  get isConnected(): boolean {
    return !!(this.config?.active && this.config?.gusto_company_uuid);
  }

  get connectionBadgeClass(): string {
    return this.isConnected ? 'badge bg-success' : 'badge bg-danger';
  }

  get connectionLabel(): string {
    return this.isConnected ? 'Connected' : 'Not Connected';
  }

  // ── Pay periods ──────────────────────────────────────────────────────────
  payPeriods: any[] = [];
  activePeriod: any = null;   // first "open" (future) period
  pastPeriods: any[] = [];    // historical / ready-for-review
  isSyncingHistory = false;
  histStartDate: string = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 60);
    return d.toISOString().split('T')[0];
  })();
  histEndDate: string = new Date().toISOString().split('T')[0];

  // ── Timesheet audit ──────────────────────────────────────────────────────
  timesheetAudit: any = null;

  // ── Incomplete onboardings ────────────────────────────────────────────────
  incompleteOnboardings: any[] = [];
  incompleteOnboardingsTotal = 0;
  onboardingActionInProgress: { [id: string]: boolean } = {};
  onboardingSteps: { [id: string]: any[] } = {};
  onboardingStepsVisible: { [id: string]: boolean } = {};

  // ── GL Mappings ──────────────────────────────────────────────────────────
  glMappings: any[] = [];
  accounts: any[] = [];
  newRow: any = {gusto_account_description: '', local_account_id: null, debit_credit: ''};
  showAddRow = false;
  activeGlRow: any = null;

  get totalDebits(): number {
    return this.glMappings
      .filter(r => r.debit_credit === 'D' && r.local_account_id)
      .reduce((s, r) => s + (r.amount ?? 0), 0);
  }

  get totalCredits(): number {
    return this.glMappings
      .filter(r => r.debit_credit === 'C' && r.local_account_id)
      .reduce((s, r) => s + (r.amount ?? 0), 0);
  }

  get glBalanced(): boolean {
    return Math.abs(this.totalDebits - this.totalCredits) < 0.005;
  }

  get unmappedCount(): number {
    return this.glMappings.filter((r: any) => r.requires_mapping).length;
  }

  // ── Event log ────────────────────────────────────────────────────────────
  eventLog: any[] = [];
  logPage = 1;
  logPageSize = 20;
  logTotal = 0;
  logEventTypeFilter = '';
  logStatusFilter = '';

  constructor(
    private titleService: Title,
    private tableDataService: DataService,
    private notification: BsToastService,
    private appConfig: AppConfigService,
    private http: HttpClient,
    private confirm: ConfirmDialogService,
  ) {
  }

  ngOnInit() {
    this.titleService.setTitle('Gusto Dashboard');
    this.apiBaseUrl = this.appConfig.apiBaseUrl;
    this.loadConfig();
    this.loadPayPeriods();
    this.loadTimesheetAudit();
    this.loadIncompleteOnboardings();
    this.loadAccounts();
    this.loadMappings();
    this.loadEventLog();
  }

  // ── Loaders ──────────────────────────────────────────────────────────────

  loadConfig() {
    this.tableDataService.getData('pr_provider_config').subscribe({
      next: (result: any) => {
        this.config = result;
      },
      error: () => {
        this.config = {active: false};
      },
    });
  }

  loadPayPeriods() {
    this.tableDataService.getData('Gusto/open-pay-period').subscribe({
      next: (result: any) => {
        const periods: any[] = result?.pay_periods ?? [];
        this.activePeriod = periods.length > 0 ? periods[0] : null;
        this.payPeriods = periods;
      },
      error: () => {
      },
    });
  }

  loadHistoricalPayPeriods() {
    if (!this.histStartDate || !this.histEndDate) {
      this.notification.showWarningToast('Please select a start date and end date before syncing.');
      return;
    }
    this.buttonsDisabled = true;
    this.isSyncingHistory = true;
    this.http.post<any>(
      `${this.apiBaseUrl}Gusto/sync-payroll-history?startDate=${this.histStartDate}&endDate=${this.histEndDate}`,
      null
    ).subscribe({
      next: () => {
        this.http.get<any>(`${this.apiBaseUrl}Gusto/payroll-report?startDate=${this.histStartDate}&endDate=${this.histEndDate}`).subscribe({
          next: (result: any) => {
            const allRows: any[] = result?.data ?? [];
            const seen = new Set<string>();
            this.pastPeriods = allRows.filter(r => {
              if (seen.has(r.payroll_run_id)) {
                return false;
              }
              seen.add(r.payroll_run_id);
              return true;
            });
            this.buttonsDisabled = false;
            this.isSyncingHistory = false;
          },
          error: () => {
            this.pastPeriods = [];
            this.buttonsDisabled = false;
            this.isSyncingHistory = false;
          },
        });
      },
      error: () => {
        this.notification.showWarningToast('Unable to sync historical payroll data.');
        this.buttonsDisabled = false;
        this.isSyncingHistory = false;
      },
    });
  }

  loadTimesheetAudit() {
    this.tableDataService.getData('Gusto/timesheet-audit').subscribe({
      next: (result: any) => {
        this.timesheetAudit = result;
      },
      error: () => {
        this.timesheetAudit = null;
      },
    });
  }

  loadIncompleteOnboardings() {
    this.http.get<any>(this.apiBaseUrl + 'Gusto/incomplete-onboardings').subscribe({
      next: (result: any) => {
        this.incompleteOnboardings = result?.employees ?? [];
        this.incompleteOnboardingsTotal = this.incompleteOnboardings.filter(
          (e: any) => e.gusto_onboarding_status !== 'onboarding_completed'
        ).length;
      },
      error: () => {
        this.incompleteOnboardings = [];
        this.incompleteOnboardingsTotal = 0;
      },
    });
  }

  onboardingStatusLabel(emp: any): string {
    if (!emp.pushed_to_gusto) {
      return 'Not Pushed to Gusto';
    }
    switch (emp.gusto_onboarding_status) {
      case null:
      case undefined:
        return 'Never Pushed';
      case 'not_started':
        return 'Not Started';
      case 'self_onboarding_invited':
        return 'Invite Sent';
      case 'in_progress':
        return 'In Progress';
      case 'onboarding_completed':
        return 'Onboarding Complete';
      default:
        return emp.gusto_onboarding_status;
    }
  }

  onboardingStatusBadge(emp: any): string {
    if (!emp.pushed_to_gusto) {
      return 'badge bg-secondary';
    }
    switch (emp.gusto_onboarding_status) {
      case null:
      case undefined:
        return 'badge bg-secondary';
      case 'not_started':
        return 'badge bg-danger';
      case 'self_onboarding_invited':
        return 'badge bg-warning text-dark';
      case 'in_progress':
        return 'badge bg-info text-dark';
      case 'onboarding_completed':
        return 'badge bg-success';
      default:
        return 'badge bg-secondary';
    }
  }

  pushEmployeeToGusto(emp: any) {
    this.confirm.confirmThis(
      [$localize`:ConfirmPushEmployee@@confirmPushEmployee:Push ${emp.first_name} ${emp.last_name} to Gusto?`],
      () => {
        this.onboardingActionInProgress[emp.id] = true;
        this.http.post<any>(this.apiBaseUrl + `Gusto/push-employee/${emp.id}`, {}).subscribe({
          next: () => {
            this.notification.showSuccessToast(`${emp.first_name} ${emp.last_name} pushed to Gusto.`);
            this.onboardingActionInProgress[emp.id] = false;
            this.loadIncompleteOnboardings();
          },
          error: (err) => {
            const msg = err?.error?.message ?? `Error pushing ${emp.first_name} ${emp.last_name} to Gusto.`;
            this.notification.showErrorToast(msg);
            this.onboardingActionInProgress[emp.id] = false;
          },
        });
      },
      () => {
      }
    );
  }

  checkOnboardingStatus(emp: any) {
    this.onboardingActionInProgress[emp.id] = true;
    this.http.get<any>(this.apiBaseUrl + `Gusto/onboarding-status/${emp.id}`).subscribe({
      next: (status: any) => {
        emp.gusto_onboarding_status = status?.onboarding_status ?? emp.gusto_onboarding_status;
        this.onboardingActionInProgress[emp.id] = false;
        this.incompleteOnboardingsTotal = this.incompleteOnboardings.filter(
          (e: any) => e.gusto_onboarding_status !== 'onboarding_completed'
        ).length;
        const steps = (status?.onboarding_steps ?? []).filter((s: any) => s.required);
        this.onboardingSteps[emp.id] = steps;
        this.onboardingStepsVisible[emp.id] = steps.length > 0;
      },
      error: (err) => {
        const msg = err?.error?.message ?? `Error checking onboarding status for ${emp.first_name} ${emp.last_name}.`;
        this.notification.showErrorToast(msg);
        this.onboardingActionInProgress[emp.id] = false;
      },
    });
  }

  inviteToOnboard(emp: any) {
    this.confirm.confirmThis(
      [$localize`:ConfirmInviteOnboard@@confirmInviteOnboard:Send onboarding invite to ${emp.first_name} ${emp.last_name} (${emp.email})?`],
      () => {
        this.onboardingActionInProgress[emp.id] = true;
        this.http.post<any>(this.apiBaseUrl + `Gusto/invite-to-onboard/${emp.id}`, {}).subscribe({
          next: () => {
            this.notification.showSuccessToast(`Onboarding invite sent to ${emp.first_name} ${emp.last_name}.`);
            this.onboardingActionInProgress[emp.id] = false;
            this.loadIncompleteOnboardings();
          },
          error: (err) => {
            const msg = err?.error?.message ?? `Error sending invite to ${emp.first_name} ${emp.last_name}.`;
            this.notification.showErrorToast(msg);
            this.onboardingActionInProgress[emp.id] = false;
          },
        });
      },
      () => {
      }
    );
  }

  loadAccounts() {
    this.tableDataService.getApiListData('account', '?state=active&pageNumber=0&pageSize=999999').subscribe({
      next: (res: any) => {
        this.accounts = res?.Data ?? res ?? [];
      },
      error: (err) => console.error(err),
    });
  }

  loadMappings() {
    this.http.get<any[]>(this.apiBaseUrl + 'Gusto/gl-account-mappings').subscribe({
      next: (rows) => {
        this.glMappings = (rows || []).map((r: any) => ({...r}));
      },
      error: (err) => console.error(err),
    });
  }

  loadEventLog() {
    let query = `?pageNumber=${this.logPage}&pageSize=${this.logPageSize}`;
    if (this.logEventTypeFilter) {
      query += `&event_type=${encodeURIComponent(this.logEventTypeFilter)}`;
    }
    if (this.logStatusFilter) {
      query += `&status=${encodeURIComponent(this.logStatusFilter)}`;
    }
    this.tableDataService.getApiListData('Gusto/event-log', query).subscribe({
      next: (result: any) => {
        this.eventLog = result?.Data ?? (result as any)?.data ?? [];
        this.logTotal = result?.totalRecords ?? result?.TotalRecords ?? 0;
      },
      error: () => {
        this.eventLog = [];
      },
    });
  }

  // ── Actions ──────────────────────────────────────────────────────────────

  connectGusto() {
    if (!this.config?.client_id || !this.config?.redirect_uri) {
      this.notification.showErrorToast($localize`:MissingGustoCredentials@@missingGustoCredentials:Please save Client ID and Redirect URI before connecting.`);
      return;
    }
    this.confirm.confirmThis(
      [$localize`:ConfirmConnectGusto@@confirmConnectGusto:Connect to Gusto? You will be redirected to Gusto's authorization page.`],
      () => {
        const env = this.config?.gusto_environment ?? 'demo';
        this.http.get<any>(this.apiBaseUrl + 'Gusto/connect?environment=' + env).subscribe({
          next: () => {
            const isDemo = this.config?.gusto_environment !== 'production';
            const baseUrl = isDemo ? 'https://api.gusto-demo.com' : 'https://api.gusto.com';
            const params = new URLSearchParams({
              client_id: this.config.client_id,
              redirect_uri: this.config.redirect_uri,
              response_type: 'code',
            });
            window.location.href = `${baseUrl}/oauth/authorize?${params.toString()}`;
          },
          error: () => {
            this.notification.showErrorToast($localize`:GustoConnectError@@gustoConnectError:Failed to initiate Gusto connection.`);
          },
        });
      },
      () => {
      }
    );
  }

  syncLocations() {
    if (this.buttonsDisabled) {
      return;
    }
    this.confirm.confirmThis(
      [$localize`:ConfirmSyncLocations@@confirmSyncLocations:Sync locations from Gusto?`],
      () => {
        this.buttonsDisabled = true;
        this.tableDataService.post('Gusto/sync-locations', {}).subscribe({
          next: () => {
            this.notification.showSuccessToast($localize`:Locations synced@@locationsSynced:Locations synced`);
            this.buttonsDisabled = false;
          },
          error: () => {
            this.notification.showErrorToast($localize`:Error syncing locations@@errorSyncingLocations:Error syncing locations`);
            this.buttonsDisabled = false;
          },
        });
      },
      () => {
      }
    );
  }

  syncPayroll() {
    if (this.buttonsDisabled) {
      return;
    }
    this.confirm.confirmThis(
      [$localize`:ConfirmSyncPayroll@@confirmSyncPayroll:Sync payroll data from Gusto? This may take a moment.`],
      () => {
        this.buttonsDisabled = true;
        this.http.post<{ message: string }>(this.apiBaseUrl + 'Gusto/sync-payroll', {}).subscribe({
          next: (res) => {
            this.notification.showSuccessToast(res?.message ?? $localize`:PayrollSynced@@payrollSynced:Payroll synced from Gusto.`);
            this.buttonsDisabled = false;
            this.loadConfig();
          },
          error: (err) => {
            const msg = err?.error?.message ?? err?.message ?? $localize`:ErrorSyncingPayroll@@errorSyncingPayroll:Error syncing payroll from Gusto.`;
            this.notification.showErrorToast(msg);
            this.buttonsDisabled = false;
          },
        });
      },
      () => {
      }
    );
  }

  postGl() {
    if (this.buttonsDisabled) {
      return;
    }
    this.confirm.confirmThis(
      [$localize`:ConfirmPostGL@@confirmPostGL:Post GL entries to AcctX from Gusto payroll data?`],
      () => {
        this.buttonsDisabled = true;
        this.isPostingGl = true;
        this.http.post<{ message: string }>(this.apiBaseUrl + 'Gusto/sync-gl', {}).subscribe({
          next: (res) => {
            this.notification.showSuccessToast(res?.message ?? $localize`:GLPosted@@glPosted:GL posted to AcctX.`);
            this.buttonsDisabled = false;
            this.isPostingGl = false;
            this.loadMappings();
          },
          error: (err) => {
            const msg = err?.error?.message ?? err?.message ?? $localize`:GLPostError@@glPostError:GL sync failed.`;
            this.notification.showErrorToast(msg);
            this.buttonsDisabled = false;
            this.isPostingGl = false;
          },
        });
      },
      () => {
      }
    );
  }

  runAutoCheckSync() {
    if (this.buttonsDisabled) {
      return;
    }
    this.confirm.confirmThis(
      [$localize`:ConfirmAutoCheckSync@@confirmAutoCheckSync:Run Automation Check Sync for historical pay periods?`],
      () => {
        this.buttonsDisabled = true;
        this.http.get<{ message: string }>(this.apiBaseUrl + 'Gusto/open-pay-period', {}).subscribe({
          next: (res) => {
            this.notification.showSuccessToast(res?.message ?? $localize`:AutoCheckSyncDone@@autoCheckSyncDone:Automation check sync complete.`);
            this.buttonsDisabled = false;
            this.loadPayPeriods();
            this.loadEventLog();
          },
          error: (err) => {
            const msg = err?.error?.message ?? err?.message ?? $localize`:AutoCheckSyncError@@autoCheckSyncError:Automation check sync failed.`;
            this.notification.showErrorToast(msg);
            this.buttonsDisabled = false;
          },
        });
      },
      () => {
      }
    );
  }

  // ── GL Mapping helpers ───────────────────────────────────────────────────

  getRowType(row: any): 'Debit' | 'Credit' | null {
    if (!row.debit_credit) {
      return null;
    }
    return row.debit_credit === 'D' ? 'Debit' : 'Credit';
  }

  getAcctType(acct: any): 'Debit' | 'Credit' {
    return acct?.debit === true ? 'Debit' : 'Credit';
  }

  isMismatch(row: any): boolean {
    if (!row.local_account_id) {
      return false;
    }
    const gustoType = this.getRowType(row);
    if (!gustoType) {
      return false;
    }
    const acct = this.accounts.find((a: any) => a.id === row.local_account_id);
    if (acct == null) {
      return false;
    }
    return this.getAcctType(acct) !== gustoType;
  }

  getAccountLabel(row: any): string {
    if (!row.local_account_id) {
      return '— None —';
    }
    const acct = this.accounts.find((a: any) => a.id === row.local_account_id);
    return acct ? `${acct.number} - ${acct.name}` : '— None —';
  }

  openAccountModal(row: any) {
    this.activeGlRow = row;
  }

  onGlAccountSelected(accountId: string) {
    if (!this.activeGlRow) {
      return;
    }
    this.activeGlRow.local_account_id = accountId;
  }

  saveMapping(row: any) {
    const dto: any = {gusto_account_description: row.gusto_account_description, local_account_id: row.local_account_id};
    if (row.debit_credit) {
      dto.debit_credit = row.debit_credit;
    }
    this.http.post<{ message: string }>(this.apiBaseUrl + 'Gusto/gl-account-mappings', dto).subscribe({
      next: () => this.loadMappings(),
      error: (err) => {
        const msg = err?.error?.message ?? err?.message ?? $localize`:SaveMappingError@@saveMappingError:Error saving mapping.`;
        this.notification.showErrorToast(msg);
      },
    });
  }

  saveAllMappings() {
    const rows = this.glMappings.filter(r => r.local_account_id);
    if (!rows.length) {
      this.notification.showWarningToast($localize`:NoMappingsToSave@@noMappingsToSave:No mapped accounts to save.`);
      return;
    }
    let completed = 0;
    rows.forEach(row => {
      const dto = {gusto_account_description: row.gusto_account_description, local_account_id: row.local_account_id};
      this.http.post<{ message: string }>(this.apiBaseUrl + 'Gusto/gl-account-mappings', dto).subscribe({
        next: () => {
          completed++;
          if (completed === rows.length) {
            this.notification.showSuccessToast($localize`:AllMappingsSaved@@allMappingsSaved:All mappings saved.`);
            this.loadMappings();
          }
        },
        error: (err) => {
          const msg = err?.error?.message ?? err?.message ?? $localize`:SaveMappingError@@saveMappingError:Error saving mapping.`;
          this.notification.showErrorToast(msg);
        },
      });
    });
  }

  addNewRow() {
    if (!this.newRow.gusto_account_description.trim()) {
      return;
    }
    this.saveMapping(this.newRow);
    this.newRow = {gusto_account_description: '', local_account_id: null, debit_credit: ''};
    this.showAddRow = false;
  }

  confirmRemoveRow(row: any) {
    this.confirm.confirmThis(
      [$localize`:ConfirmRemoveRow@@confirmRemoveRow:Remove this GL mapping row?`],
      () => this.removeRow(row),
      () => {
      }
    );
  }

  removeRow(row: any) {
    if (row.id) {
      this.http.delete(this.apiBaseUrl + 'Gusto/gl-account-mappings/' + row.id).subscribe({
        next: () => this.glMappings = this.glMappings.filter(r => r !== row),
        error: (err) => {
          const msg = err?.error?.message ?? err?.message ?? $localize`:DeleteMappingError@@deleteMappingError:Error deleting mapping.`;
          this.notification.showErrorToast(msg);
        },
      });
    } else {
      this.glMappings = this.glMappings.filter(r => r !== row);
    }
  }

  // ── Event log helpers ────────────────────────────────────────────────────

  logEventBadgeClass(status: string): string {
    switch ((status ?? '').toLowerCase()) {
      case 'success':
        return 'badge bg-success';
      case 'error':
        return 'badge bg-danger';
      case 'warning':
        return 'badge bg-warning text-dark';
      default:
        return 'badge bg-secondary';
    }
  }

  logEventIcon(status: string): string {
    switch ((status ?? '').toLowerCase()) {
      case 'success':
        return 'bi bi-check-circle-fill text-success';
      case 'error':
        return 'bi bi-x-circle-fill text-danger';
      case 'warning':
        return 'bi bi-exclamation-triangle-fill text-warning';
      default:
        return 'bi bi-info-circle text-secondary';
    }
  }

  onLogFilterChange() {
    this.logPage = 1;
    this.loadEventLog();
  }

  get canPushAllToGusto(): boolean {
    if (!this.timesheetAudit) {
      return false;
    }
    const a = this.timesheetAudit;
    return (a.pending_push ?? 0) > 0 &&
      (a.draft ?? 0) === 0 &&
      (a.pending_approval ?? 0) === 0 &&
      (a.returned ?? 0) === 0;
  }

  pushAllApprovedToGusto() {
    this.confirm.confirmThis(
      [$localize`:ConfirmPushAllGusto@@confirmPushAllGusto:Push all approved timesheets for this pay period to Gusto?`],
      () => {
        this.tableDataService.getApiListData('Timesheet', '?status=approved&pending_gusto=true&pageNumber=1&pageSize=9999').subscribe({
          next: (result: any) => {
            const rows: any[] = result?.Data ?? (result as any)?.data ?? [];
            const ids = rows.map((r: any) => r.id);
            if (!ids.length) {
              this.notification.showWarningToast($localize`:NoPendingTimesheets@@noPendingTimesheets:No approved timesheets pending push found.`);
              return;
            }
            this.http.post<any>(this.apiBaseUrl + 'Gusto/bulk-push-timesheets', ids).subscribe({
              next: (res: any) => {
                const results: any[] = res?.results ?? [];
                const failed = results.filter(r => !r.success);
                if (failed.length === 0) {
                  this.notification.showSuccessToast($localize`:PushAllSuccess@@pushAllSuccess:All timesheets pushed to Gusto successfully.`);
                } else {
                  this.notification.showWarningToast(`${failed.length} timesheet(s) failed to push. Check the event log for details.`);
                }
                this.loadTimesheetAudit();
              },
              error: (err) => {
                const msg = err?.error?.message ?? err?.message ?? $localize`:PushAllError@@pushAllError:Error pushing timesheets to Gusto.`;
                this.notification.showErrorToast(msg);
              },
            });
          },
          error: () => {
            this.notification.showErrorToast($localize`:FetchTimesheetsError@@fetchTimesheetsError:Error fetching approved timesheets.`);
          },
        });
      },
      () => {
      }
    );
  }

  // ── Utility ──────────────────────────────────────────────────────────────

  formatAuditDate(dateStr: string | null | undefined): string {
    if (!dateStr) {
      return '—';
    }
    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const parts = datePart.split('-').map(Number);
    if (parts.length < 3 || parts.some(isNaN)) {
      return dateStr;
    }
    const [y, m, d] = parts;
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
  }

  parseDate(dateStr: string): Date {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  formatDate(dateStr: string): string {
    if (!dateStr) {
      return '';
    }
    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const [y, m, d] = datePart.split('-').map(Number);
    if (!y || !m || !d || isNaN(y) || isNaN(m) || isNaN(d)) {
      return dateStr;
    }
    return `${String(m).padStart(2, '0')}/${String(d).padStart(2, '0')}/${y}`;
  }
}
